import { z } from 'zod';
import type { Rating } from '@/types';

export const ratingSchema = z
  .number()
  .int()
  .min(1)
  .max(5) as z.ZodType<Rating>;

export const customerDetailsSchema = z.object({
  customerName: z
    .string()
    .min(1, 'Customer name is required')
    .max(200, 'Customer name must be less than 200 characters')
    .trim(),
  companyName: z
    .string()
    .min(1, 'Company name is required')
    .max(200, 'Company name must be less than 200 characters')
    .trim(),
  productOrService: z
    .string()
    .min(1, 'Product/Service is required')
    .max(200, 'Product/Service must be less than 200 characters')
    .trim(),
  engagementDate: z
    .string()
    .min(1, 'Engagement date is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  contactPerson: z
    .string()
    .min(1, 'Contact person is required')
    .max(200, 'Contact person must be less than 200 characters')
    .trim(),
});

export const satisfactionRatingsSchema = z.object({
  productQuality_expectationsMet: ratingSchema,
  productQuality_performanceAsExpected: ratingSchema,
  delivery_timeliness: ratingSchema,
  delivery_processEfficient: ratingSchema,
  communication_clearTimely: ratingSchema,
  communication_promptResponses: ratingSchema,
  staff_professionalCourteousHelpful: ratingSchema,
  staff_supportAdequateEffective: ratingSchema,
  price_valueForMoney: ratingSchema,
  overall_overallSatisfied: ratingSchema,
  overall_wouldRecommend: ratingSchema,
});

export const openEndedSchema = z.object({
  likesMost: z
    .string()
    .min(1, 'This field is required')
    .max(2000, 'Response must be less than 2000 characters')
    .trim(),
  areasForImprovement: z
    .string()
    .min(1, 'This field is required')
    .max(2000, 'Response must be less than 2000 characters')
    .trim(),
  wouldDoBusinessAgain: z
    .string()
    .min(1, 'This field is required')
    .max(2000, 'Response must be less than 2000 characters')
    .trim(),
});

export const consentSchema = z.object({
  agreed: z.boolean().refine((val) => val === true, {
    message: 'You must agree to the consent statement to submit the survey',
  }),
  timestamp: z.string(),
});

export const surveyFormSchema = z.object({
  details: customerDetailsSchema,
  ratings: satisfactionRatingsSchema,
  open: openEndedSchema,
  consent: consentSchema,
});

export type SurveyFormData = z.infer<typeof surveyFormSchema>;

